import { useEffect, useState } from 'react';
import { staffApi, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { PlusCircle, Eye, EyeOff } from 'lucide-react';

const emptyForm = {
  code: '',
  description: '',
  discountType: 'PERCENTAGE',
  discountValue: '',
  startDate: '',
  expiryDate: '',
  usageLimit: '',
};

export default function DiscountCodesTab() {
  const [discounts, setDiscounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [showHidden, setShowHidden] = useState(false);

  const load = () => staffApi.get('/discounts').then(({ data }) => setDiscounts(data.discounts));

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (d) => {
    setEditing(d);
    setForm({
      code: d.code,
      description: d.description || '',
      discountType: d.discountType,
      discountValue: d.discountValue,
      startDate: d.startDate ? d.startDate.slice(0, 10) : '',
      expiryDate: d.expiryDate ? d.expiryDate.slice(0, 10) : '',
      usageLimit: d.usageLimit ?? '',
    });
    setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      if (editing) await staffApi.patch(`/discounts/${editing.id}`, form);
      else await staffApi.post('/discounts', form);
      toast.success('Discount code saved');
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (d) => {
    await staffApi.patch(`/discounts/${d.id}`, { isActive: !d.isActive });
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button
          data-testid="discounts-show-hidden-toggle"
          onClick={() => setShowHidden((v) => !v)}
          className="text-sm text-muted-ink hover:text-charcoal flex items-center gap-1.5 transition-colors duration-150"
        >
          {showHidden ? <EyeOff size={14} /> : <Eye size={14} />}
          {showHidden ? 'Hide' : 'Show'} Hidden ({discounts.filter((d) => !d.isActive).length})
        </button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="discount-create-btn" onClick={openCreate} className="bg-saffron hover:bg-saffron-hover">
              <PlusCircle size={16} className="mr-2" /> New Discount Code
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="discount-form-dialog">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit Discount Code' : 'Create Discount Code'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Code</Label>
                <Input
                  data-testid="discount-code-field"
                  disabled={!!editing}
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  placeholder="WELCOME10"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Description</Label>
                <Textarea data-testid="discount-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Type</Label>
                  <Select value={form.discountType} onValueChange={(v) => setForm({ ...form, discountType: v })}>
                    <SelectTrigger data-testid="discount-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PERCENTAGE">Percentage</SelectItem>
                      <SelectItem value="FIXED">Fixed Amount</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Value</Label>
                  <Input type="number" data-testid="discount-value-input" value={form.discountValue} onChange={(e) => setForm({ ...form, discountValue: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Start Date</Label>
                  <Input type="date" data-testid="discount-startdate-input" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Expiry Date</Label>
                  <Input type="date" data-testid="discount-expirydate-input" value={form.expiryDate} onChange={(e) => setForm({ ...form, expiryDate: e.target.value })} />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label>Usage Limit (blank = unlimited)</Label>
                  <Input type="number" data-testid="discount-usagelimit-input" value={form.usageLimit} onChange={(e) => setForm({ ...form, usageLimit: e.target.value })} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="discount-save-btn" disabled={busy} onClick={save} className="bg-saffron hover:bg-saffron-hover">
                {busy ? 'Saving…' : 'Save Discount Code'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-xl border border-border/40 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-bone/60">
              <TableHead>Code</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Value</TableHead>
              <TableHead>Usage</TableHead>
              <TableHead>Expiry</TableHead>
              <TableHead>Active</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {discounts.filter((d) => showHidden || d.isActive).map((d) => (
              <TableRow key={d.id} data-testid="discount-row">
                <TableCell className="font-medium">{d.code}</TableCell>
                <TableCell>{d.discountType}</TableCell>
                <TableCell>{d.discountType === 'PERCENTAGE' ? `${d.discountValue}%` : `$${Number(d.discountValue).toFixed(2)}`}</TableCell>
                <TableCell>{d.usageCount}{d.usageLimit !== null ? ` / ${d.usageLimit}` : ''}</TableCell>
                <TableCell>{d.expiryDate ? new Date(d.expiryDate).toLocaleDateString('en-CA') : '—'}</TableCell>
                <TableCell>
                  <Switch data-testid={`discount-active-toggle-${d.id}`} checked={d.isActive} onCheckedChange={() => toggleActive(d)} />
                </TableCell>
                <TableCell>
                  <button data-testid={`discount-edit-btn-${d.id}`} onClick={() => openEdit(d)} className="text-sm text-coriander hover:underline">
                    Edit
                  </button>
                </TableCell>
              </TableRow>
            ))}
            {discounts.filter((d) => showHidden || d.isActive).length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-ink py-8">No discount codes yet.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
